import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";

import Text from "components/Text";
import { useApprovalsAndRequests } from "repositories/office-repository";
import {
  updateTimesheetStatus,
  updateEditTimesheetRequestStatus,
} from "repositories/timesheet-repository";
import { cancelLeave, updateLeaveStatus } from "repositories/leave-repository";
import styles from "./../Offices.module.scss";
import useHandleApiError from "hooks/useHandleApiError";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "components/Loader";
import toast from "react-hot-toast";
import Empty from "components/Empty";
import "./../Offices.scss";
import crossIcon from "../../../../assets/images/cross.svg";
import { Modal, ModalBody, Spinner } from "reactstrap";
import RequestApprovalCard from "./RequestApprovalCard";
import {
  acceptEventRequest,
  rejectEventRequest,
} from "repositories/scheduler-repository";
import constants from "../../../../constants";
const PAGE_SIZE = 100;

const RequestApprovalModal = ({
  t,
  isRequestApprovalModalOpen,
  setIsRequestApprovalModalOpen,
  officeId,
  onUpdate,
  ownerId
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [loadData, setLoadData] = useState(false);
  const [pageData, setPageData] = useState({
    recentUpdatesList: [],
    totalItems: 0,
    totalPages: 1,
  });
  const {
    data,
    error: isError,
    isLoading,
    isFetching,
    refetch,
  } = useApprovalsAndRequests(officeId,ownerId, pageNumber, PAGE_SIZE);
  const { recentUpdatesList, totalItems, totalPages } = pageData;

  /* use to handle API errors */
  useHandleApiError(isLoading, isFetching, isError);

  const closeRequestApprovalModal = () => setIsRequestApprovalModalOpen(false);
  useEffect(() => {
    if (!isLoading && data?.data?.recentUpdates) {
      setPageData((prev) => {
        return {
          totalItems: data?.pagination?.totalItems,
          totalPages: data?.pagination?.totalPages,
          recentUpdatesList: [...data.data.recentUpdates],
        };
      });
    }
  }, [isLoading, data, pageNumber]);

  useEffect(() => {
    const value = data?.data?.count || 0;
    onUpdate(value);
  }, [data?.data?.count]);

  const acceptRejectTimesheet = async (timesheetId, statusId, reason) => {
    setLoadData(true);
    try {
      const response = await updateTimesheetStatus({
        timesheetId: timesheetId?.toString(),
        statusId: statusId,
        reason: reason ? reason : null,
      });

      setPageData((prev) => ({
        ...prev,
        recentUpdatesList: prev.recentUpdatesList.filter(
          (item) => item.timesheetId !== timesheetId
        ),
      }));
      if (response?.message) {
        toast.success(response.message);
      }
      refetch();
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setLoadData(false);
    }
  };

  const acceptRejectLeave = async ({ actionTypeId, isApproved, Comment }) => {
    setLoadData(true);
    try {
      const response = await updateLeaveStatus({
        id: actionTypeId.toString(),
        isAccepted: isApproved,
        Comment,
      });
      setPageData((prev) => ({
        ...prev,
        recentUpdatesList: prev.recentUpdatesList.filter(
          (item) => item.leave_Id !== actionTypeId
        ),
      }));
      if (response?.message) {
        toast.success(response.message);
      }
      refetch();
      setLoadData(false);
    } catch (error) {
      toast.error(error?.message);
      setLoadData(false);
    }
  };

  const acceptRejectEditTimesheetRequest = async (actionTypeId, isApproved) => {
    setLoadData(true);
    try {
      await updateEditTimesheetRequestStatus(actionTypeId, isApproved);
      setPageData((prev) => ({
        ...prev,
        recentUpdatesList: prev.recentUpdatesList.filter(
          (item) => item.clockInOutTimeSheetEditRequestId !== actionTypeId
        ),
      }));
      refetch();
      setLoadData(false);
    } catch (error) {
      toast.error(error?.message);
      setLoadData(false);
    }
  };

  const acceptRejectShiftEvent = async (actionTypeId, officeId, isApproved) => {
    setLoadData(true);
    try {
      if (isApproved) {
        await acceptEventRequest(actionTypeId, officeId);
      } else {
        await rejectEventRequest(actionTypeId, officeId);
      }
      setPageData((prev) => ({
        ...prev,
        recentUpdatesList: prev.recentUpdatesList.filter(
          (item) => item.clockInOutTimeSheetEditRequestId !== actionTypeId
        ),
      }));

      refetch();
      setLoadData(false);
    } catch (error) {
      toast.error(error?.message);
      setLoadData(false);
    }
  };
  const staffCancelLeaves = async (
    actionTypeId,
    officeId,
    isApproved,
    reason = ""
  ) => {
    setLoadData(true);
    try {
      const params = {
        id: actionTypeId.toString(),
        Comment: reason,
        IsStaff: false,
        IsCancel: isApproved,
        officeId: officeId,
      };
      let res = await cancelLeave(params);
      if (res.status) {
        toast.success(res.message);
        refetch();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadData(false);
    }
  };

  return (
    <Modal
      isOpen={isRequestApprovalModalOpen}
      toggle={closeRequestApprovalModal}
      className={
        "modal-dialog-centered  modal-width-660 approval-modal " +
        styles["approval-modal"]
      }
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={closeRequestApprovalModal}>
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody id="scrollableDiv">
        <div
          className={
            "modal-custom-title title-location-center mw-100 " +
            styles["modal-custom-title"]
          }
        >
          <Text size="25px" marginBottom="40px" weight="500" color="#111b45">
            <span className="modal-title-25">
              {t("staffTimesheet.requestandApprovals")}
              {data?.data?.count > 0 && <span>({data?.data?.count})</span>}
            </span>
          </Text>
        </div>
        <div className={styles["card-container"]}>
          {(isLoading || isFetching || loadData) && <Loader />}
          {recentUpdatesList && recentUpdatesList.length > 0 ? (
            <InfiniteScroll
              dataLength={recentUpdatesList.length}
              hasMore={recentUpdatesList.length < totalItems}
              next={() =>
                pageNumber < totalPages && setPageNumber((v) => v + 1)
              }
              scrollableTarget="scrollableDiv"
              loader={
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" className="loader-spinner" />
                </div>
              }
            >
              {recentUpdatesList.map((updateList) => (
                <div key={updateList.createdOn}>
                  {updateList && (
                    <RequestApprovalCard
                      officeId={officeId}
                      list={updateList}
                      handleAction={(
                        actionType,
                        actionTypeId,
                        statusId,
                        isApproved,
                        commentForLeaveState
                      ) => {
                        switch (actionType) {
                          case "Timesheet":
                            acceptRejectTimesheet(actionTypeId, statusId);

                            break;
                          case "Leave":
                            if (
                              updateList.leave_Status ===
                              constants.LeaveStatus[6].value
                            ) {
                              staffCancelLeaves(
                                actionTypeId,
                                officeId,
                                isApproved,
                                commentForLeaveState.trim()
                              );
                            } else {
                              acceptRejectLeave({
                                actionTypeId,
                                isApproved,
                                Comment: commentForLeaveState.trim(),
                              });
                            }
                            break;
                          case "EditTimesheetRequest":
                            acceptRejectEditTimesheetRequest(
                              actionTypeId,
                              isApproved
                            );
                            break;
                          case "shiftEvent":
                            acceptRejectShiftEvent(
                              actionTypeId,
                              officeId,
                              isApproved
                            );
                            break;

                          default:
                            break;
                        }
                      }}
                    />
                  )}
                </div>
              ))}
            </InfiniteScroll>
          ) : (
            <Empty Message={t("staffTimesheet.emptyMsg")} />
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default withTranslation()(RequestApprovalModal);
